import { getTimelineStatusAt as getTimelineStatus } from '@/helpers/ssr/dates'

import type { Sbir } from '@/types/sbir/Sbir'

import type { BadgeVariant } from '../../common/BadgeV2'
import type { BreadcrumbItem } from '../../common/BreadcrumbsV2'
import {
  type NormalizedFile,
  formatDate,
  formatFileSize,
  normalizeDepartmentName,
  normalizeFiles,
} from '../../common/transforms'

// ============================================================================
// Types
// ============================================================================

export interface NormalizedSbirData {
  // Header
  breadcrumbs: BreadcrumbItem[]
  title: string
  subtitle: string
  badges: Array<{
    label: string
    variant: BadgeVariant
  }>

  // Key Stats
  stats: {
    agency: string
    branch: string
    phase: string
    program: string
    status: string
    postedDate: string | null
    closeDate: string | null
    applicationDueDate: string | null
  }

  // Timeline
  timeline: Array<{
    date: string
    label: string
    status: 'past' | 'current' | 'future'
  }>

  // Content
  description: string
  aiSummary: string | null

  // Files
  files: NormalizedFile[]

  // External Links
  externalLinks: {
    sbirTopicLink: string | null
    solicitationAgencyUrl: string | null
  }

  // Similar Opportunities
  similarCount: number
}

// ============================================================================
// Domain-Specific Utilities
// ============================================================================

/**
 * Map SBIR status to badge variant
 */
function getStatusVariant(status: string): BadgeVariant {
  const statusLower = status.toLowerCase()
  if (statusLower === 'open' || statusLower === 'active') return 'success'
  if (statusLower === 'closed') return 'neutral'
  if (statusLower === 'closing soon') return 'warning'
  return 'default'
}

/**
 * Map program type to badge variant
 */
function getProgramVariant(program: string): BadgeVariant {
  const programUpper = program.toUpperCase()
  if (programUpper === 'SBIR') return 'primary'
  if (programUpper === 'STTR') return 'info'
  return 'default'
}

// ============================================================================
// Main Transform
// ============================================================================

/**
 * Normalize raw SBIR data into a format suitable for DisplayV2 components
 */
export function normalizeSbirData(
  sbir: Sbir,
  referenceMs: number,
): NormalizedSbirData {
  // Build breadcrumbs
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'SBIR/STTR', href: '/sbirs/search' },
    { label: sbir.number, current: true, isCode: true },
  ]

  // Build badges
  const badges: Array<{ label: string; variant: BadgeVariant }> = []

  // Status badge
  if (sbir.status) {
    badges.push({
      label: sbir.status,
      variant: getStatusVariant(sbir.status),
    })
  }

  // Program badge (SBIR/STTR)
  if (sbir.program) {
    badges.push({
      label: sbir.program.toUpperCase(),
      variant: getProgramVariant(sbir.program),
    })
  }

  // Phase badge
  if (sbir.phase) {
    badges.push({
      label: sbir.phase,
      variant: 'outline',
    })
  }

  // Build timeline
  const timeline: NormalizedSbirData['timeline'] = []

  if (sbir.release_date) {
    timeline.push({
      date: sbir.release_date,
      label: 'Release Date',
      status: getTimelineStatus(sbir.release_date, referenceMs),
    })
  }

  if (sbir.open_date) {
    timeline.push({
      date: sbir.open_date,
      label: 'Open Date',
      status: getTimelineStatus(sbir.open_date, referenceMs),
    })
  }

  if (sbir.application_due_date) {
    timeline.push({
      date: sbir.application_due_date,
      label: 'Application Due',
      status: getTimelineStatus(sbir.application_due_date, referenceMs, true),
    })
  }

  if (sbir.close_date) {
    timeline.push({
      date: sbir.close_date,
      label: 'Close Date',
      status: getTimelineStatus(sbir.close_date, referenceMs, true),
    })
  }

  // Sort timeline by date
  timeline.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  )

  // Normalize files
  const files = normalizeFiles(sbir.files)

  // Build subtitle
  const closeDate = formatDate(sbir.close_date)
  const subtitle = closeDate
    ? `Response deadline: ${closeDate}`
    : `Posted: ${formatDate(sbir.posted_date) ?? 'Unknown'}`

  return {
    breadcrumbs,
    title: sbir.title,
    subtitle,
    badges,

    stats: {
      agency: normalizeDepartmentName(sbir.agency) ?? sbir.agency,
      branch: normalizeDepartmentName(sbir.branch) ?? sbir.branch,
      phase: sbir.phase,
      program: sbir.program,
      status: sbir.status,
      postedDate: formatDate(sbir.posted_date),
      closeDate: formatDate(sbir.close_date),
      applicationDueDate: formatDate(sbir.application_due_date),
    },

    timeline,

    description: sbir.description,
    aiSummary: sbir.ai_summary || null,

    files,

    externalLinks: {
      sbirTopicLink: sbir.sbir_topic_link || null,
      solicitationAgencyUrl: sbir.solicitation_agency_url || null,
    },

    similarCount: sbir.similar_sbirs?.length ?? 0,
  }
}
